import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtDateTimeFirm } from "@/lib/dt";
import { askConfirm } from "@/lib/appDialog";
import { LockKeyhole, Unlock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Batch 22 · PR B · Follow-up 2 (owner 14-Oct-2026, item 43) — the active sign-in locks a Partner / Admin (their firm) or the
 * Founder (every firm + addresses) can see, with one Unlock action per lock. The server decides who may clear what
 * (`POST /auth/locks/unlock`, step-up required — the global step-up dialog handles the 428); the window counter is kept, so
 * the next failed attempt locks again at once. Rendered on Settings → Team members and Platform → Users. Hidden while the
 * list is empty (nothing to clear), with a small refresh link so an admin can look again.
 */
export default function SignInLocksPanel({ testid = "signin-locks" }) {
  const colW = useResizableColumns({ storageKey: "sign-in-locks-panel", defaults: [240, 160, 160, 160, 130], mins: [130, 100, 100, 100, 90] });   // Step 11d · add-on
  const { t } = useI18n();
  const [locks, setLocks] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/auth/locks", { _silent: true });
      setLocks(Array.isArray(r.data?.locks) ? r.data.locks : []);
    } catch { setLocks([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const unlock = async (scope, key, label) => {
    const ok = await askConfirm(t("settings.locks.confirm").replace("{who}", label), { title: t("settings.locks.unlock") });
    if (!ok) return;
    setBusy(`${scope}:${key}`);
    try {
      await api.post("/auth/locks/unlock", { scope, key });
      toast.success(t("settings.locks.unlocked").replace("{who}", label));
      await load();
    } catch (e) { toast.error(toErrMsg(e, t("settings.locks.unlockFailed"))); }
    finally { setBusy(""); }
  };

  if (!locks || locks.length === 0) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground" data-testid={`${testid}-empty`}>
        <LockKeyhole className="h-3 w-3" /> {t("settings.locks.none")}
        <button type="button" className="underline" onClick={load} data-testid={`${testid}-refresh`}>{t("settings.locks.refresh")}</button>
      </div>
    );
  }
  return (
    <Card data-testid={testid}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2"><LockKeyhole className="h-4 w-4 text-amber-600" /> {t("settings.locks.title")}</CardTitle>
        <Button size="sm" variant="ghost" onClick={load} data-testid={`${testid}-refresh`}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </CardHeader>
      <CardContent className="p-0">
        <p className="px-4 pb-2 text-[11px] text-muted-foreground">{t("settings.locks.hint")}</p>
        <ResizableTable cols={colW} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
            <tr>
              <ResizableTh index={0} className="px-4 py-2 text-start">{t("settings.locks.colWhat")}</ResizableTh>
              <ResizableTh index={1} className="px-4 py-2 text-start">{t("settings.locks.colAddress")}</ResizableTh>
              <ResizableTh index={2} className="px-4 py-2 text-start">{t("settings.locks.colSince")}</ResizableTh>
              <ResizableTh index={3} className="px-4 py-2 text-start">{t("settings.locks.colUntil")}</ResizableTh>
              <ResizableTh index={4} className="px-4 py-2 text-end">{t("common.actions")}</ResizableTh>
            </tr>
          </thead>
          <tbody>
            {locks.map((l) => {
              const id = `${l.scope}:${l.key}`;
              const label = l.kind === "ip" ? `${t("settings.locks.address")} ${l.key}` : (l.user_name ? `${l.user_name} · ${l.key}` : l.key);
              return (
                <tr key={id} className="border-b" data-testid={`${testid}-row-${l.kind}-${l.key}`}>
                  <td className="px-4 py-2 font-medium">
                    {l.kind === "ip" ? <span>{t("settings.locks.address")} <span className="font-mono" dir="ltr">{l.key}</span></span>
                      : <span>{l.user_name ? `${l.user_name} · ` : ""}<span dir="ltr">{l.key}</span></span>}
                    <span className="ms-2 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">{t("settings.locks.kind." + (l.scope || "login_account"))}</span>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs" dir="ltr">
                    {l.kind === "ip" ? "—" : (l.ip || "—")}
                    {l.ip_locked && <span className="ms-2 rounded-md bg-rose-50 px-1.5 py-0.5 font-sans text-[10px] text-rose-700" data-testid={`${testid}-iplocked-${l.key}`}>{t("settings.locks.addressLockedToo")}</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">{fmtDateTimeFirm(l.locked_at)}</td>
                  <td className="px-4 py-2 text-xs">{fmtDateTimeFirm(l.unlock_at)}</td>
                  <td className="px-4 py-2 text-end whitespace-nowrap space-x-1">
                    <Button size="sm" variant="outline" disabled={busy === id} onClick={() => unlock(l.scope, l.key, label)}
                            data-testid={`${testid}-unlock-${l.kind}-${l.key}`}>
                      <Unlock className="me-1 h-3.5 w-3.5" /> {t("settings.locks.unlock")}
                    </Button>
                    {l.ip_locked && l.kind !== "ip" && (
                      <Button size="sm" variant="ghost" disabled={busy === `login_ip:${l.ip}`}
                              onClick={() => unlock("login_ip", l.ip, `${t("settings.locks.address")} ${l.ip}`)}
                              data-testid={`${testid}-unlock-ip-${l.ip}`}>
                        <Unlock className="me-1 h-3.5 w-3.5" /> {t("settings.locks.unlockAddress")}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ResizableTable>
      </CardContent>
    </Card>
  );
}
